import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { Language } from "../domain/enums";
import { t as tFn, type StringKey } from "../domain/i18n";

const LS_KEY = "pp.lang";

interface LanguageCtx {
  lang: Language;
  setLang: (l: Language) => void;
  t: (key: StringKey, vars?: Record<string, string | number>) => string;
}

const Ctx = createContext<LanguageCtx | null>(null);

/**
 * UA-only mode: language is hard-locked to "uk". The i18n dictionaries for
 * RU/EN remain in the codebase for the admin/CSV pipeline, but the gameplay
 * UI never shows anything other than Ukrainian.
 *
 * To re-enable multilingual UI later, restore the original LanguagePicker
 * route and the Settings → Language section.
 */
const FORCED_LANG: Language = "uk";

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Language>(FORCED_LANG);

  // Wipe any previously saved RU/EN choice so we never auto-load the wrong locale.
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, FORCED_LANG);
    } catch {
      /* ignore */
    }
    document.documentElement.lang = FORCED_LANG;
  }, []);

  const setLang = useCallback((_l: Language) => {
    // Intentionally a no-op while UA-only mode is active.
    setLangState(FORCED_LANG);
  }, []);

  const t = useCallback(
    (key: StringKey, vars?: Record<string, string | number>) => tFn(lang, key, vars),
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLanguage(): LanguageCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("LanguageProvider missing");
  return v;
}
